// useSaveStatus — surface autosave (fire-and-forget) failures to the user instead
// of swallowing them to console (Q-SAVE). Wrap a save call; on rejection the hook
// holds an error message the row can render inline, auto-clearing after a delay.

import { useCallback, useRef, useState } from "react";

export interface SaveStatus {
  /** Non-null while a save error is being shown. */
  error: string | null;
  /** true while a wrapped save is in flight. */
  saving: boolean;
  /** true briefly after a successful save (transient — auto-clears). Lets a
   *  page show a "Saved ✓" confirmation, the success counterpart to `error`. */
  saved: boolean;
  /** Wrap an async save; resolves to true on success, false on failure. */
  run: (fn: () => Promise<unknown>, label?: string) => Promise<boolean>;
  /** Manually clear the current error. */
  clear: () => void;
}

export function useSaveStatus(autoClearMs = 6000): SaveStatus {
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // How long the "Saved ✓" confirmation lingers (independent of the longer
  // error linger).
  const SAVED_MS = 2000;

  const clear = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    setError(null);
  }, []);

  const run = useCallback(
    async (fn: () => Promise<unknown>, label = "save"): Promise<boolean> => {
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
      if (savedTimer.current) {
        clearTimeout(savedTimer.current);
        savedTimer.current = null;
      }
      setError(null);
      setSaved(false);
      setSaving(true);
      try {
        await fn();
        setSaved(true);
        savedTimer.current = setTimeout(() => setSaved(false), SAVED_MS);
        return true;
      } catch (e) {
        const detail = e instanceof Error ? e.message : String(e);
        const msg = `Couldn't ${label} — change not saved. ${detail}`;
        console.error(`[${label}] failed`, e);
        setError(msg);
        if (autoClearMs > 0) {
          timer.current = setTimeout(() => setError(null), autoClearMs);
        }
        return false;
      } finally {
        setSaving(false);
      }
    },
    [autoClearMs],
  );

  return { error, saving, saved, run, clear };
}
